/**
 * Counts reps of a movement that goes down and comes back, rather than one that
 * revolves.
 *
 * This is the other half of the rep-counting problem. `metrics/revolutions.js`
 * accumulates the signed arm angle, which is right for the swing and wrong for
 * everything else: a squat, a press, a hinge or a push-up never revolve, so
 * their accumulated angle sums to zero over a rep and the counter would never
 * fire. What they do instead is leave a resting value, reach an extreme, and
 * come back, so what counts them is a threshold crossed in both directions.
 *
 * Two thresholds rather than one, because a single one counts the noise. The
 * knee angle wanders a degree or two at the bottom of a squat, and a counter
 * watching one line would score every wobble across it as another rep. With a
 * gap between the two lines the signal has to travel the whole way back before
 * it can count again, which is the standard way out and costs nothing: the gap
 * is wider than the noise floor by construction.
 *
 * The rep boundary is the TOP, not the bottom. A rep that starts at the top
 * carries its whole descent, which is the half the posture criteria mostly read,
 * and it is also where the athlete is standing still, so the boundary does not
 * land in the middle of fast movement.
 *
 * `tailMs` exists because that boundary cuts an explosive movement in the wrong
 * place. `high` has to sit below a locked joint for a jump to count at all —
 * the athlete does not lock out between reps — so the rep closes partway up the
 * push, and the extension at the top of the jump, which is the part worth
 * judging, falls outside the window. With a tail the counter keeps collecting
 * past the crossing until the signal falls back below `high` or the tail runs
 * out, so the rep carries its own flight. The cap is what makes the last rep of
 * a set arrive: the athlete then stands there with the knee above `high`, and
 * without it the signal never comes back down and the rep never closes.
 */

export class OscillationCounter {
  /**
   * @param options.signal Reads the scalar being watched out of a sample, given
   *   the near side. Degrees, metres, anything monotonic in the movement.
   * @param options.low The signal has to go below this for the rep to be under
   *   way: the depth that makes it a rep rather than a twitch.
   * @param options.high And back above this to close it. Must sit clear of
   *   `low` by more than the signal's noise.
   * @param options.visibility Reads the visibility that gates the frame, the way
   *   `RepCounter` gates on arm and trunk: a landmark the model is guessing at
   *   can cross a threshold on its own.
   * @param options.minVisibility Frames below this are refused outright.
   * @param options.maxGapMs A longer gap between frames abandons the rep in
   *   progress rather than bridging it, since the movement may have completed
   *   unobserved.
   * @param options.tailMs Keep collecting for up to this long after the signal
   *   crosses `high`, so a rep carries what happens past the crossing. 0, the
   *   default, closes on the crossing itself.
   * @param options.accept Optional last word on whether a closed rep was really
   *   a rep, given its samples and the side. The signal alone cannot always
   *   tell: a knee bending on the floor traces the same curve as a knee bending
   *   to jump, so the squat jump asks for flight in the samples before counting
   *   one. A rejected rep is not counted, not scored and not reported — the
   *   athlete settling himself between reps should cost nothing — which is only
   *   safe because what it rejects is a movement the drill does not claim to
   *   judge, rather than a rep performed badly.
   */
  constructor({
    signal,
    low,
    high,
    visibility,
    minVisibility = 0.5,
    maxGapMs = 250,
    tailMs = 0,
    accept = null,
  }) {
    this.signal = signal;
    this.low = low;
    this.high = high;
    this.visibility = visibility;
    this.minVisibility = minVisibility;
    this.maxGapMs = maxGapMs;
    this.tailMs = tailMs;
    this.accept = accept;
    this.reset();
  }

  reset() {
    this.count = 0;
    /** The most recent usable reading, for the progress bar. */
    this.value = null;
    /** "waiting" until the signal has been seen up top at least once, then
     *  "top" between reps, "down" once it has gone below `low`, and "tail"
     *  while a closed rep is still collecting its flight. */
    this.phase = "waiting";
    this.last = null;
    this.current = [];
    this.lastRep = null;
    /** When the signal crossed `high`, while the tail is being collected. */
    this.crossedAt = null;
    /** Movements that closed but `accept` turned down, for the record. */
    this.rejected = 0;
  }

  /**
   * Feeds one sample in. Returns the completed rep's samples when this sample
   * closed one, otherwise null. Same contract as `RepCounter.push`, so the
   * caller does not care which counter an exercise uses.
   */
  push(sample, side) {
    if (this.visibility(sample, side) < this.minVisibility) {
      this.break();
      return null;
    }
    const value = this.signal(sample, side);
    if (!Number.isFinite(value)) {
      this.break();
      return null;
    }
    if (this.last != null && sample.t - this.last > this.maxGapMs) {
      this.break();
    }
    this.last = sample.t;
    this.lastSide = side;
    this.value = value;
    this.current.push(sample);

    if (this.phase === "waiting") {
      // Only ever armed from the top, so a take that begins with the athlete
      // already at the bottom does not count the way up as a whole rep.
      if (value >= this.high) {
        this.phase = "top";
        this.current = [sample];
      }
      return null;
    }

    if (this.phase === "top") {
      if (value <= this.low) {
        this.phase = "down";
      } else {
        // Still standing: hold the boundary at the last frame up top rather
        // than banking however long the athlete stood there.
        this.current = [sample];
      }
      return null;
    }

    if (this.phase === "tail") {
      // The extension is over once the signal falls back below the line it
      // crossed, and the cap ends a tail that never does — the last rep of a
      // set, where the athlete simply stands there above `high`.
      if (value >= this.high && sample.t - this.crossedAt < this.tailMs) {
        return null;
      }
      return this.closeRep(sample, value);
    }

    if (value < this.high) {
      return null;
    }
    if (this.tailMs > 0) {
      this.phase = "tail";
      this.crossedAt = sample.t;
      return null;
    }
    return this.closeRep(sample, value);
  }

  /**
   * Banks the rep in progress. The closing sample starts the next one, except
   * when a tail has already carried the signal back down past `high`: the
   * athlete is then on the way into the next rep rather than standing at the
   * top, and holding the boundary at this frame would drop the beginning of
   * that descent.
   */
  closeRep(sample, value) {
    const closed = this.current;
    this.crossedAt = null;
    this.phase = value <= this.low ? "down" : "top";
    this.current = [sample];
    if (this.accept != null && !this.accept(closed, this.lastSide)) {
      this.rejected += 1;
      return null;
    }
    this.count += 1;
    this.lastRep = closed;
    return this.lastRep;
  }

  /** The side the last sample was read on, for `flush`. */
  get side() {
    return this.lastSide;
  }

  /**
   * Closes a rep that is still collecting its tail, and returns its samples.
   *
   * The tail waits either for the signal to come back down or for its cap to
   * run out, and both need frames that stop arriving the moment a set ends. The
   * athlete pressing Stop right after their last jump would otherwise lose that
   * rep from the count and from the ticket, which is the one thing a rep
   * counter must not do. Returns null when there is nothing waiting, so the
   * caller can flush unconditionally at the end of a set.
   */
  flush() {
    if (this.phase !== "tail") {
      return null;
    }
    const last = this.current[this.current.length - 1];
    return this.closeRep(last, this.signal(last, this.side) ?? this.value);
  }

  /** Abandons the rep in progress, keeping the count so far. */
  break() {
    this.phase = "waiting";
    this.value = null;
    this.last = null;
    this.current = [];
    this.crossedAt = null;
  }

  /**
   * How far through the current rep, 0 to 1.
   *
   * Half the bar for the way down and half for the way back, so it fills once
   * per rep rather than twice, and it is read off the current depth rather than
   * the deepest frame so that it tracks the athlete coming back up.
   */
  get progress() {
    if (this.value == null || this.phase === "waiting") {
      return 0;
    }
    const span = this.high - this.low;
    if (span <= 0) {
      return this.phase === "down" ? 0.5 : 0;
    }
    const height = Math.min(1, Math.max(0, (this.value - this.low) / span));
    // A tail is the end of a rep, not the start of the next one, so the bar
    // stays full through it rather than snapping back to empty mid-flight.
    if (this.phase === "tail") {
      return 1;
    }
    return this.phase === "down" ? 0.5 + 0.5 * height : 0.5 * (1 - height);
  }
}
